import React, { useEffect, useMemo, useState } from "react";
import { CSVIcon, DeleteIcon, DisableDeleteIcon, EditIcon, EnableDeleteIcon, ListOptionsIcon } from "../assets/Icons";
import AddCheckpoint from "./modals/AddCheckpoint";
import QuestionSelectionListModal from "./modals/QuestionSelectionListModal";
import DeleteItem from "./modals/DeleteItem";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import axios from "axios";
import { useParams } from "react-router-dom";
import QuestionCSV from "./modals/QuestionCSV";
import Modal from "react-bootstrap/Modal";

const CheckList = ({ setQestionLength, setQuestionMapping }) => {
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [addCheckpoint, setAddCheckpoint] = useState(false);
  const [deleteCheckpoint, setDeleteCheckpoint] = useState(false);
  const [selected, setSelected] = useState([]);
  const [deleteSingle, setDeleteSingle] = useState(false);
  const [editQuestion, seEditQuestion] = useState({});
  const [questions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { uuid } = useParams();
  const [CSVModal, setCSVModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveQuestionId, setInactiveQuestionId] = useState(null);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savedOrder, setSavedOrder] = useState([]);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [selectionQuestion, setSelectionQuestion] = useState(null);
  const [selectionQuestionIndex, setSelectionQuestionIndex] = useState(0);

  const fetchQuestions = async () => {
    try {
      let { data } = await axios.get(`${configData.config.API_URL}/api/machine/questions/all/${uuid}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
      setAllQuestions(data.data);
      const mapping = {};
      data.data?.forEach((item) => {
        mapping[item.uuid] = item.title;
      });
      setQuestionMapping(mapping);
      setQestionLength(data.data.length);
      setSavedOrder((data.data || []).map((q) => q.uuid));
      setOrderDirty(false);
      setLoading(false);
    } catch (error) {
      console.log("error", error);
    }
    setCSVModal(false);
    setProcessing(false);
  };

  const confirmInactiveQuestion = (id) => {
    setInactiveQuestionId(id);
    setShowInactiveModal(true);
  };

  const handleInactiveQuestion = async () => {
    try {
      await axios.put(
        `${configData.config.API_URL}/api/machine/questions/inActive/${inactiveQuestionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      fetchQuestions();
      setShowInactiveModal(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleSelect = (id, checked) => {
    setSelected(checked ? [...selected, id] : selected.filter((e) => e !== id));
  };

  const handleSelectAll = () => {
    setSelected(selected.length === questions.length ? [] : questions.map((e) => e.uuid));
  };

  const handleEditQuestion = (elem, index) => {
    setAddCheckpoint(true);
    seEditQuestion({ active: true, data: elem, index });
  };

  const handleOpenSelectionModal = (elem, index) => {
    setSelectionQuestion(elem);
    setSelectionQuestionIndex(index);
    setSelectionModalOpen(true);
  };

  const isSelectionListEnabled = (question) =>
    Boolean(question?.selectionConfig?.enabled && question?.selectionConfig?.options?.length);

  useEffect(() => {
    setSelected(selected.length === questions.length ? questions.map((e) => e.uuid) : selected);
  }, [questions.length]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const canDrag = dragEnabled && !savingOrder;

  const savedOrderIndex = useMemo(() => {
    const map = new Map();
    savedOrder.forEach((id, idx) => map.set(id, idx));
    return map;
  }, [savedOrder]);

  const revertToSavedOrder = () => {
    setAllQuestions((prev) => {
      if (!savedOrder || savedOrder.length === 0) return prev;
      const byId = new Map(prev.map((q) => [q.uuid, q]));
      const next = [];
      savedOrder.forEach((id) => {
        const q = byId.get(id);
        if (q) next.push(q);
      });
    
      prev.forEach((q) => {
        if (!savedOrderIndex.has(q.uuid)) next.push(q);
      });
      return next;
    });
    setOrderDirty(false);
    setDraggingId(null);
    setDragOverId(null);
  };

  const onToggleReorder = () => {
    setDragEnabled((prev) => {
      const next = !prev;
      if (prev === true && next === false) {
        if (orderDirty) revertToSavedOrder();
        setDraggingId(null);
        setDragOverId(null);
      }
      return next;
    });
  };

  const saveOrder = async () => {
    try {
      setSavingOrder(true);
      const orderedUuids = questions.map((q) => q.uuid);

      // NOTE: This is a single bulk call. Backend should persist order using this list.
      await axios.put(
        `${configData.config.API_URL}/api/machine/questions/reorder/${uuid}`,
        { orderedUuids },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSavedOrder(orderedUuids);
      setOrderDirty(false);
      setDragEnabled(false);
    } catch (error) {
      console.log("error", error);
    } finally {
      setSavingOrder(false);
      setDraggingId(null);
      setDragOverId(null);
    }
  };

  const onDragStartRow = (id) => (e) => {
    if (!canDrag) return;
    setDraggingId(id);
    setDragOverId(null);
    // helps some browsers render a better drag preview
    try {
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // ignore
    }
  };

  const onDragOverRow = (id) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (dragOverId !== id) setDragOverId(id);
  };

  const onDropRow = (targetId) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    setAllQuestions((prev) => {
      const fromIndex = prev.findIndex((q) => q.uuid === draggingId);
      const toIndex = prev.findIndex((q) => q.uuid === targetId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setOrderDirty(true);

    setDraggingId(null);
    setDragOverId(null);
  };

  const onDragEndRow = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const canRearrangeQuestions = questions.length >= 2;
  const rearrangeButtonTitle = canRearrangeQuestions
    ? "Enable rearranging questions"
    : "Add more questions to rearrange";

  
  const saveOrderData = async () => {
    try {
      setSavingOrder(true);
      const questionsPayload = questions.map((q, index) => ({
        uuid: q.uuid,
        priority: index + 1,
      }));

      await axios.post(
        `${configData.config.API_URL}/api/machine/questions/update/questions-priority`,
        { questions: questionsPayload },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSavedOrder(questions.map((q) => q.uuid));
      setOrderDirty(false);
      setDragEnabled(false);
    } catch (error) {
      console.log("error", error);
    } finally {
      setSavingOrder(false);
      setDraggingId(null);
      setDragOverId(null);
    }
  };
  return (
    <>
      <div className="checklist">
        {!loading && (
          <>
            {questions.length > 0 && (
              <>
                <div className="list-head" style={{ height: "35px" }}>
                  <div className="form-check listchild">
                    {/* <>
                      <input
                        onChange={handleSelectAll}
                        className='form-check-input'
                        type='checkbox'
                        checked={selected.length === questions.length}
                        id='selectAllCheck'
                      /> 

                      <label className='form-check-label' htmlFor='selectAllCheck'>
                        {selected.length} / {questions.length}
                      </label>
                      <div className='svgbtn' style={{ marginTop: '-1px' }} onClick={() => { setDeleteCheckpoint(true); setDeleteSingle(false) }}>
                        {
                          selected.length > 0 ? <EnableDeleteIcon /> : <DisableDeleteIcon />
                        }
                      </div>
                    </> */}
                  </div>

                  {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") && (
                    <div className="d-flex gap-3">
                      {!dragEnabled ? (
                        <button
                          className="plain-btn icon"
                          onClick={onToggleReorder}
                          disabled={savingOrder || loading || !canRearrangeQuestions}
                          title={rearrangeButtonTitle}
                        >
                          <span className="reorder-btn-icon " style={{marginRight: '1.5px', marginBottom: '2px'}} aria-hidden="true">
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                          </span>
                          Rearrange Questions
                        </button>
                      ) : (
                        <>
                          <button
                            className="plain-btn secondary"
                            onClick={onToggleReorder}
                            disabled={savingOrder}
                            title="Cancel reordering"
                          >
                            Cancel
                          </button>
                          <button
                            className="plain-btn"
                            onClick={saveOrderData}
                            disabled={!orderDirty || savingOrder}
                            title={orderDirty ? "Save this order" : "Reorder rows to enable saving"}
                          >
                            {savingOrder ? "Saving…" : "Save Question Order"}
                          </button>
                        </>
                      )}
                      <button className="plain-btn icon" onClick={() => setCSVModal(true)}>
                        <CSVIcon />
                        Upload CSV
                      </button>
                      <button className="plain-btn" onClick={() => setAddCheckpoint(true)}>
                        Add Question
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
        {loading ? (
          <>
            <div className="list-body mt-3 p-0">
              <div className="table-responsive workflowtb mt-2">
                <table className="table align-middle table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th className="text-center">S.NO</th>
                      <th>Questions</th>
                      <th className="text-center">Tags</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center">
                        <Skeleton width="3vw" height="1.65rem" />
                      </td>
                      <td>
                        {" "}
                        <Skeleton width="20vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center">
                        <Skeleton width="3vw" height="1.65rem" />
                      </td>
                      <td>
                        {" "}
                        <Skeleton width="20vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-center">
                        <Skeleton width="3vw" height="1.65rem" />
                      </td>
                      <td>
                        {" "}
                        <Skeleton width="20vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                      <td className="text-center">
                        {" "}
                        <Skeleton width="5vw" height="1.65rem" />{" "}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            {questions.length > 0 ? (
              <div className="list-body mt-3 p-0">
                <div className="table-responsive workflowtb mt-2">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th className="text-center">S.NO</th>
                        <th>Questions</th>
                        <th className="text-center">Tags</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.length > 0 &&
                        questions.map((elem, index) => (
                          <tr
                            key={elem.uuid}
                            draggable={canDrag}
                            onDragStart={onDragStartRow(elem.uuid)}
                            onDragOver={onDragOverRow(elem.uuid)}
                            onDrop={onDropRow(elem.uuid)}
                            onDragEnd={onDragEndRow}
                            className={[
                              "checklist-row-draggable",
                              canDrag ? "is-enabled" : "is-disabled",
                              draggingId === elem.uuid ? "is-dragging" : "",
                              dragOverId === elem.uuid && draggingId && draggingId !== elem.uuid ? "is-drag-over" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                <div className="form-check listchild">
                                  {/* <input
                                  onChange={(e) => handleSelect(elem.uuid, e.target.checked)}
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={selected.includes(elem.uuid)}
                                  id={`check-${elem.uuid}`}
                                /> */}
                                  <label className="form-check-label" htmlFor={`check-${elem.uuid}`}>
                                    {dragEnabled && (
                                      <span
                                        className="checklist-drag-handle"
                                        title="Drag to reorder"
                                        aria-hidden="true"
                                      >
                                        ⋮⋮
                                      </span>
                                    )}
                                    {index + 1}
                                  </label>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="question-title-cell">
                                <span>{elem.title}</span>
                                {isSelectionListEnabled(elem) && (
                                  <span className="selection-list-badge" title="Mobile popup list configured">
                                    List enabled · {elem.selectionConfig.options.length}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="details-tags attable">
                                <span>
                                  {elem.tags.length
                                    ? elem.tags.map((e) => (
                                        <p key={e.id} className="tag">
                                          {e.name}
                                        </p>
                                      ))
                                    : null}
                                </span>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="action-btns">
                                {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") && (
                                  <span onClick={() => handleEditQuestion(elem, index)}>
                                    <EditIcon />
                                  </span>
                                )}
                                {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") && (
                                  <span
                                    className={isSelectionListEnabled(elem) ? "selection-list-action active" : "selection-list-action"}
                                    title="Configure mobile popup list"
                                    onClick={() => handleOpenSelectionModal(elem, index)}
                                  >
                                    <ListOptionsIcon active={isSelectionListEnabled(elem)} />
                                  </span>
                                )}
                                <span style={{ cursor: "pointer" }} onClick={() => confirmInactiveQuestion(elem.uuid)}>
                                  <DeleteIcon />
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <>
                {userData?.user?.permissions?.includes("MACHINE.QUESTIONS.ADD") ? (
                  <>
                    <div className="row">
                      <div className="px-4">
                        <div className="first-event">
                          No Question added yet!
                          <div className="d-flex gap-3">
                            <button className="plain-btn icon" onClick={() => setCSVModal(true)}>
                              <CSVIcon />
                              Upload CSV
                            </button>
                            <button className="plain-btn" onClick={() => setAddCheckpoint(true)}>
                              Add Question
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </>
        )}
      </div>

      <AddCheckpoint
        addCheckpoint={addCheckpoint}
        setAddCheckpoint={setAddCheckpoint}
        fetchQuestions={fetchQuestions}
        size={questions.length}
        editQuestion={editQuestion}
        seEditQuestion={seEditQuestion}
      />

      <QuestionSelectionListModal
        open={selectionModalOpen}
        onClose={() => {
          setSelectionModalOpen(false);
          setSelectionQuestion(null);
        }}
        question={selectionQuestion}
        questionIndex={selectionQuestionIndex}
        onSaved={fetchQuestions}
      />

      <DeleteItem
        setSelected={setSelected}
        selected={selected}
        deleteCheckpoint={deleteCheckpoint}
        fetchQuestions={fetchQuestions}
        setDeleteCheckpoint={setDeleteCheckpoint}
        deleteSingle={deleteSingle}
      />

      <QuestionCSV
        fetchQuestions={fetchQuestions}
        machineId={uuid}
        CSVModal={CSVModal}
        setCSVModal={setCSVModal}
        setProcessing={setProcessing}
        processing={processing}
      />

      <Modal centered show={showInactiveModal} onHide={() => setShowInactiveModal(false)}>
        <Modal.Header className="popupheader delete pt-4">Delete Question</Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12">
              <div className="form-group pt-4 pb-5 m-0">
                <p className="popupheading delete p-0 m-0">Do you really want to delete this question</p>
              </div>
              <div className="row">
                <div className="col-12 d-flex justify-content-center mt-2 mb-2 footerbtn">
                  <button className="cancel" onClick={() => setShowInactiveModal(false)}>
                    Cancel
                  </button>
                  <button className="save" onClick={handleInactiveQuestion}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CheckList;
